import { type CreateInspectionInput } from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { listBuildings, listUnits } from '../../../api/buildings.js';
import { listInspectionTemplates } from '../../../api/inspection-templates.js';
import { createInspection } from '../../../api/inspections.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  onClose: () => void;
  /** When set, locks the building and pre-fills the form. */
  buildingId?: string;
}

interface FormState {
  templateId: string;
  buildingId: string;
  unitId: string;
  scheduledFor: string;
  notes: string;
}

const EMPTY: FormState = {
  templateId: '',
  buildingId: '',
  unitId: '',
  scheduledFor: '',
  notes: '',
};

export function CreateInspectionSheet({ onClose, buildingId: lockedBuildingId }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const templateFieldId = useId();
  const buildingFieldId = useId();
  const unitFieldId = useId();
  const scheduledFieldId = useId();
  const notesFieldId = useId();

  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    buildingId: lockedBuildingId ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'all'],
    queryFn: () => listBuildings({ limit: 100 }),
    enabled: !lockedBuildingId,
  });

  const unitsQuery = useQuery({
    queryKey: ['building', form.buildingId, 'units'],
    queryFn: () => listUnits(form.buildingId),
    enabled: Boolean(form.buildingId),
  });

  const templatesQuery = useQuery({
    queryKey: ['inspection-templates', 'published'],
    queryFn: () => listInspectionTemplates({ status: ['published'] }),
  });

  const selectedTemplate = useMemo(
    () => templatesQuery.data?.templates.find((t) => t.id === form.templateId) ?? null,
    [templatesQuery.data, form.templateId],
  );

  const submit = useMutation({
    mutationFn: async () => {
      const input: CreateInspectionInput = {
        templateId: form.templateId,
        buildingId: form.buildingId,
        unitId: form.unitId || null,
        inspectorUserId: null,
        scheduledFor: form.scheduledFor ? new Date(form.scheduledFor).toISOString() : null,
        notes: form.notes.trim() || null,
        metadata: {},
      };
      return createInspection(input);
    },
    onSuccess: (data) => {
      toast.success('Inspection created.');
      void queryClient.invalidateQueries({ queryKey: ['inspections'] });
      navigate(`/inspections/runs/${data.inspection.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create inspection');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  const requiresUnit = selectedTemplate?.appliesTo === 'unit';

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>New inspection</SheetTitle>
          <SheetDescription>
            Pick a published template, building, and (for unit templates) a unit. Inspections start
            in <em>scheduled</em>; tap Start to begin filling them out.
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-3 overflow-y-auto" onSubmit={handleSubmit}>
          <div className="grid gap-1.5">
            <Label htmlFor={templateFieldId}>Template</Label>
            <select
              id={templateFieldId}
              required
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.templateId}
              onChange={(e) => setForm({ ...form, templateId: e.target.value })}
            >
              <option value="">Select…</option>
              {templatesQuery.data?.templates.map((t) => (
                <option key={t.id} value={t.id} disabled={t.publishedVersion === null}>
                  {t.name}
                  {t.publishedVersion === null ? ' (no published version yet)' : ''}
                </option>
              ))}
            </select>
          </div>

          {!lockedBuildingId ? (
            <div className="grid gap-1.5">
              <Label htmlFor={buildingFieldId}>Building</Label>
              <select
                id={buildingFieldId}
                required
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
                value={form.buildingId}
                onChange={(e) => setForm({ ...form, buildingId: e.target.value, unitId: '' })}
              >
                <option value="">Select…</option>
                {buildingsQuery.data?.buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {requiresUnit ? (
            <div className="grid gap-1.5">
              <Label htmlFor={unitFieldId}>Unit</Label>
              <select
                id={unitFieldId}
                required
                disabled={!form.buildingId}
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
                value={form.unitId}
                onChange={(e) => setForm({ ...form, unitId: e.target.value })}
              >
                <option value="">Select…</option>
                {unitsQuery.data?.units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.unitNumber}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={scheduledFieldId}>Scheduled for (optional)</Label>
            <Input
              id={scheduledFieldId}
              type="datetime-local"
              value={form.scheduledFor}
              onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={notesFieldId}>Notes</Label>
            <textarea
              id={notesFieldId}
              rows={3}
              maxLength={8_000}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submit.isPending ||
                !form.templateId ||
                !form.buildingId ||
                (requiresUnit && !form.unitId)
              }
            >
              {submit.isPending ? 'Creating…' : 'Create inspection'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
